import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { RefreshCw, TrendingUp, TrendingDown, Minus, Filter } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';

interface SignalProcessingRecord {
  id: string;
  bot_id: string;
  signal_token: string;
  instrument: string;
  timestamp: string;
  original_action: string;
  ai_decision: 'WIN' | 'LOSS' | 'SKIP';
  prediction_confidence: number;
  win_probability: number;
  processed_action: string | null;
  processing_status: 'KEPT' | 'REVERSED' | 'SKIPPED';
  amount: number | null;
  created_at: string;
  signal_type: 'ENTER' | 'EXIT';
  linked_signal_id: string | null;
  entry_signal_token: string | null;
  entry_processing_status: 'KEPT' | 'REVERSED' | 'SKIPPED' | null;
  bot?: {
    name: string;
  };
}

interface Bot {
  id: string;
  name: string;
}

interface ProcessingStats {
  total: number;
  kept: number;
  reversed: number;
  skipped: number;
}

export const SignalProcessingHistory: React.FC = () => {
  const [records, setRecords] = useState<SignalProcessingRecord[]>([]);
  const [bots, setBots] = useState<Bot[]>([]);
  const [selectedBotId, setSelectedBotId] = useState<string>('');
  const [processingFilter, setProcessingFilter] = useState<string>('');
  const [signalTypeFilter, setSignalTypeFilter] = useState<string>('');
  const [stats, setStats] = useState<ProcessingStats>({ total: 0, kept: 0, reversed: 0, skipped: 0 });
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const loadBots = async () => {
    try {
      const { data, error } = await supabase
        .from('bots')
        .select('id, name')
        .eq('pattern_processing_enabled', true)
        .order('name');

      if (error) throw error;
      setBots(data || []);
    } catch (error) {
      console.error('Error loading bots:', error);
      toast({
        title: "Error loading bots",
        description: "Failed to load pattern processing enabled bots",
        variant: "destructive"
      });
    }
  };

  const loadSignalHistory = async () => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('signal_processing_history')
        .select(`
          *,
          bot:bots(name)
        `)
        .order('timestamp', { ascending: false })
        .limit(100);

      if (selectedBotId && selectedBotId !== 'all') {
        query = query.eq('bot_id', selectedBotId);
      }

      if (processingFilter && processingFilter !== 'all') {
        query = query.eq('processing_status', processingFilter);
      }

      if (signalTypeFilter && signalTypeFilter !== 'all') {
        query = query.eq('signal_type', signalTypeFilter);
      }

      const { data, error } = await query;

      if (error) throw error;

      const typedData = (data || []) as SignalProcessingRecord[];
      setRecords(typedData);

      // Calculate stats
      const total = typedData.length;
      const kept = typedData.filter(r => r.processing_status === 'KEPT').length;
      const reversed = typedData.filter(r => r.processing_status === 'REVERSED').length;
      const skipped = typedData.filter(r => r.processing_status === 'SKIPPED').length;
      
      setStats({ total, kept, reversed, skipped });

    } catch (error) {
      console.error('Error loading signal history:', error);
      toast({
        title: "Error loading signal history",
        description: "Failed to load signal processing history",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBots();
  }, []);

  useEffect(() => {
    loadSignalHistory();
  }, [selectedBotId, processingFilter, signalTypeFilter]);

  const getProcessingStatusBadge = (status: string) => {
    switch (status) {
      case 'KEPT':
        return <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">KEPT</Badge>;
      case 'REVERSED':
        return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400">REVERSED</Badge>;
      case 'SKIPPED':
        return <Badge variant="secondary" className="bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400">SKIPPED</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getAiDecisionIcon = (decision: string) => {
    switch (decision) {
      case 'WIN':
        return <TrendingUp className="h-4 w-4 text-green-600" />;
      case 'LOSS':
        return <TrendingDown className="h-4 w-4 text-red-600" />;
      case 'SKIP':
        return <Minus className="h-4 w-4 text-gray-600" />;
      default:
        return null;
    }
  };

  const formatAction = (action: string) => {
    if (action.includes('LONG')) {
      return <span className="text-green-600 font-medium">{action}</span>;
    } else if (action.includes('SHORT')) {
      return <span className="text-red-600 font-medium">{action}</span>;
    }
    return <span className="font-medium">{action}</span>;
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{stats.total}</div>
            <div className="text-sm text-muted-foreground">Total Signals</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-600">{stats.kept}</div>
            <div className="text-sm text-muted-foreground">Kept ({stats.total > 0 ? Math.round((stats.kept / stats.total) * 100) : 0}%)</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-yellow-600">{stats.reversed}</div>
            <div className="text-sm text-muted-foreground">Reversed ({stats.total > 0 ? Math.round((stats.reversed / stats.total) * 100) : 0}%)</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-gray-600">{stats.skipped}</div>
            <div className="text-sm text-muted-foreground">Skipped ({stats.total > 0 ? Math.round((stats.skipped / stats.total) * 100) : 0}%)</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Actions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Signal Processing History
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={loadSignalHistory}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <Select value={selectedBotId} onValueChange={setSelectedBotId}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="All Bots" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Bots</SelectItem>
                {bots.map((bot) => (
                  <SelectItem key={bot.id} value={bot.id}>
                    {bot.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={processingFilter} onValueChange={setProcessingFilter}>
              <SelectTrigger className="w-full sm:w-[150px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="KEPT">Kept</SelectItem>
                <SelectItem value="REVERSED">Reversed</SelectItem>
                <SelectItem value="SKIPPED">Skipped</SelectItem>
              </SelectContent>
            </Select>

            <Select value={signalTypeFilter} onValueChange={setSignalTypeFilter}>
              <SelectTrigger className="w-full sm:w-[150px]">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="ENTER">Enter</SelectItem>
                <SelectItem value="EXIT">Exit</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* History Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Bot</TableHead>
                  <TableHead>Instrument</TableHead>
                  <TableHead>Original Signal</TableHead>
                  <TableHead>AI Decision</TableHead>
                  <TableHead>Processed Signal</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Confidence</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8">
                      <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                      Loading signal history...
                    </TableCell>
                  </TableRow>
                ) : records.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      No signal processing history found
                    </TableCell>
                  </TableRow>
                ) : (
                  records.map((record) => (
                    <TableRow key={record.id}>
                      <TableCell>
                        <div className="text-sm">
                          {format(new Date(record.timestamp), 'MMM dd, HH:mm:ss')}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={record.signal_type === 'ENTER' ? 'default' : 'secondary'} 
                               className={record.signal_type === 'ENTER' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400' : 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400'}>
                          {record.signal_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm font-medium">
                          {record.bot?.name || 'Unknown Bot'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm font-medium">{record.instrument}</div>
                      </TableCell>
                      <TableCell>
                        {formatAction(record.original_action)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getAiDecisionIcon(record.ai_decision)}
                          <span className="text-sm">{record.ai_decision}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {record.processed_action ? formatAction(record.processed_action) : 
                          <span className="text-muted-foreground text-sm">N/A</span>
                        }
                      </TableCell>
                      <TableCell>
                        {getProcessingStatusBadge(record.processing_status)}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {record.prediction_confidence ? 
                            `${(record.prediction_confidence * 100).toFixed(1)}%` : 
                            'N/A'
                          }
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};